import { useMemo } from "react";
import { useZeroSelector } from "../../hooks/use-zero-selector.js";
import { queries } from "../queries";
import { useCurrentUser } from "../current-user";
import { createIncrementalMapSelector } from "../incremental-map-selector";
import { useUserPresenceMap, useUserStatusEmojiMap } from "./user_presence";
import { useMapChurnLog } from "../use-map-churn-log";
import { useStableMap } from "../use-stable-map";
import type { RenderUser } from "../render-user";

// ─── Types ──────────────────────────────────────────────────────────

// Phase 1.5.3 — `status_emoji` moved to user_presence (Phase 1.5 perf
// rollout). The selector below projects identity-only RenderUser fields
// from `users` rows; status_emoji is merged in at the hook level via
// useUserPresenceMap (so the underlying source-of-truth swaps cleanly
// once PR 1.5.6 strips users.X). Identity fields don't change on
// typing heartbeats, so this selector remains stable across them.
type RenderUserIdentity = Omit<RenderUser, "status_emoji">;

type ActiveUser = RenderUser & {
  is_online?: boolean | null;
  last_active_at?: number | null;
  is_guest?: boolean | null;
  email?: string | null;
};

// Phase 1.5.3 — identity-only projection. The hot fields (is_online,
// last_active_at, status_emoji) are merged in at the hook level from
// useUserPresenceMap. is_guest stays here because it's identity.
type ActiveUserIdentity = RenderUserIdentity & {
  is_guest?: boolean | null;
};

// ─── Pure merge helpers (testable, used by the hooks below) ────────

type RenderCacheEntry = {
  src: RenderUserIdentity;
  emoji: string | null;
  merged: RenderUser;
};

/**
 * Merge identity map + presence map into a Map<id, RenderUser>, with
 * per-id reference stability when neither identity nor status_emoji
 * changed. The cache is mutated in place and reflects the latest run.
 */
export function mergeRenderUserMap(
  identityMap: Map<string, RenderUserIdentity>,
  presenceMap: Map<string, { status_emoji?: string | null }>,
  cache: Map<string, RenderCacheEntry>,
): Map<string, RenderUser> {
  const out = new Map<string, RenderUser>();
  const next = new Map<string, RenderCacheEntry>();
  for (const [id, identity] of identityMap) {
    const newEmoji = presenceMap.get(id)?.status_emoji ?? null;
    const prev = cache.get(id);
    if (prev && prev.src === identity && prev.emoji === newEmoji) {
      out.set(id, prev.merged);
      next.set(id, prev);
    } else {
      const mergedEntry: RenderUser = { ...identity, status_emoji: newEmoji };
      out.set(id, mergedEntry);
      next.set(id, { src: identity, emoji: newEmoji, merged: mergedEntry });
    }
  }
  cache.clear();
  for (const [k, v] of next) cache.set(k, v);
  return out;
}

/**
 * Same merge contract as `mergeRenderUserMap`, but for an ordered
 * list (useActiveUsers' RenderUser[] return shape).
 */
export function mergeRenderUserList(
  identityList: readonly RenderUserIdentity[],
  presenceMap: Map<string, { status_emoji?: string | null }>,
  cache: Map<string, RenderCacheEntry>,
): RenderUser[] {
  const out: RenderUser[] = [];
  const next = new Map<string, RenderCacheEntry>();
  for (const identity of identityList) {
    const id = identity.id;
    const newEmoji = presenceMap.get(id)?.status_emoji ?? null;
    const prev = cache.get(id);
    if (prev && prev.src === identity && prev.emoji === newEmoji) {
      out.push(prev.merged);
      next.set(id, prev);
    } else {
      const mergedEntry: RenderUser = { ...identity, status_emoji: newEmoji };
      out.push(mergedEntry);
      next.set(id, { src: identity, emoji: newEmoji, merged: mergedEntry });
    }
  }
  cache.clear();
  for (const [k, v] of next) cache.set(k, v);
  return out;
}

type ActiveCacheEntry = {
  src: ActiveUserIdentity;
  is_online: boolean;
  last_active_at: number | null;
  status_emoji: string | null;
  merged: ActiveUser;
};

export function mergeActiveUserMap(
  identityMap: Map<string, ActiveUserIdentity>,
  presenceMap: Map<
    string,
    {
      is_online?: boolean | null;
      last_active_at?: number | null;
      status_emoji?: string | null;
    }
  >,
  cache: Map<string, ActiveCacheEntry>,
): Map<string, ActiveUser> {
  const out = new Map<string, ActiveUser>();
  const next = new Map<string, ActiveCacheEntry>();
  for (const [id, identity] of identityMap) {
    const presence = presenceMap.get(id);
    const isOnline = presence?.is_online ?? false;
    const lastActiveAt = presence?.last_active_at ?? null;
    const statusEmoji = presence?.status_emoji ?? null;
    const prev = cache.get(id);
    if (
      prev &&
      prev.src === identity &&
      prev.is_online === isOnline &&
      prev.last_active_at === lastActiveAt &&
      prev.status_emoji === statusEmoji
    ) {
      out.set(id, prev.merged);
      next.set(id, prev);
    } else {
      const mergedEntry: ActiveUser = {
        ...identity,
        is_online: isOnline,
        last_active_at: lastActiveAt,
        status_emoji: statusEmoji,
      };
      out.set(id, mergedEntry);
      next.set(id, {
        src: identity,
        is_online: isOnline,
        last_active_at: lastActiveAt,
        status_emoji: statusEmoji,
        merged: mergedEntry,
      });
    }
  }
  cache.clear();
  for (const [k, v] of next) cache.set(k, v);
  return out;
}

// ─── Selector factories (created once per hook instance) ────────────

function createRenderUserMapSelector() {
  return createIncrementalMapSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (u: any) => u.id as string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (u: any): RenderUserIdentity => ({
      id: u.id,
      display_name: u.display_name,
      avatar_url: u.avatar_url,
      is_coworker: u.is_coworker,
      is_deactivated: u.is_deactivated,
      email: u.email,
    }),
    (prev, next) =>
      prev.display_name === next.display_name &&
      prev.avatar_url === next.avatar_url &&
      prev.is_coworker === next.is_coworker &&
      prev.is_deactivated === next.is_deactivated &&
      prev.email === next.email,
  );
}

function createUserPreviewListSelector() {
  type UserPreview = {
    id: string;
    display_name: string;
    avatar_url?: any;
    is_deactivated?: any;
  };
  let prev: UserPreview[] = [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data: any[]): UserPreview[] => {
    if (data.length !== prev.length) {
      prev = data.map((u) => ({
        id: u.id,
        display_name: u.display_name,
        avatar_url: u.avatar_url,
        is_deactivated: u.is_deactivated,
      }));
      return prev;
    }
    let changed = false;
    const next: UserPreview[] = [];
    for (let i = 0; i < data.length; i++) {
      const u = data[i];
      const p = prev[i];
      if (
        p &&
        p.id === u.id &&
        p.display_name === u.display_name &&
        p.avatar_url === u.avatar_url &&
        p.is_deactivated === u.is_deactivated
      ) {
        next.push(p);
      } else {
        changed = true;
        next.push({
          id: u.id,
          display_name: u.display_name,
          avatar_url: u.avatar_url,
          is_deactivated: u.is_deactivated,
        });
      }
    }
    if (!changed) return prev;
    prev = next;
    return next;
  };
}

// Phase 1.5.3 — identity-only projection (no status_emoji). Merge with
// useUserPresenceMap happens in the useActiveUsers hook below.
function createActiveUsersSelector() {
  let prev: RenderUserIdentity[] = [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data: any[]): RenderUserIdentity[] => {
    const active: RenderUserIdentity[] = data
      .filter((u) => !u.is_deactivated)
      .map((u) => ({
        id: u.id,
        display_name: u.display_name,
        avatar_url: u.avatar_url,
        is_coworker: u.is_coworker,
        is_deactivated: u.is_deactivated,
        email: u.email,
      }));

    if (active.length !== prev.length) {
      prev = active;
      return active;
    }

    let changed = false;
    for (let i = 0; i < active.length; i++) {
      const a = active[i];
      const p = prev[i];
      if (
        !p ||
        p.id !== a.id ||
        p.display_name !== a.display_name ||
        p.avatar_url !== a.avatar_url ||
        p.is_coworker !== a.is_coworker ||
        p.is_deactivated !== a.is_deactivated
      ) {
        changed = true;
        break;
      }
    }
    if (!changed) return prev;
    prev = active;
    return active;
  };
}

// ─── Hooks ──────────────────────────────────────────────────────────

// Module-level singleton selectors. These MUST NOT be inside hooks
// because useRef resets on component remount (Strict Mode or context
// cascade), which resets the selector's internal state (prevMap becomes
// empty → every data delivery reports CHANGED instead of STABLE).
const CACHE_KEY = "users.all";
const EMPTY_RENDER_USER_IDENTITY_MAP = new Map<string, RenderUserIdentity>();
const EMPTY_ACTIVE_USER_IDENTITY_MAP = new Map<string, ActiveUserIdentity>();
const EMPTY_RENDER_USER_IDENTITY_LIST: RenderUserIdentity[] = [];

// Module-level merge caches. Shared across all consumers of the
// hook (matches the createIncrementalMapSelector pattern above —
// there's effectively ONE logical merged view per process; all
// consumers see the same result). Keep at module level rather than
// per-hook useRef because (a) the existing comment in this file
// warns about useRef resetting on component remount which would
// reset the cache, and (b) the React rule `react-hooks/refs`
// flags ref access from inside useMemo.
// Separate per-hook caches: useRenderUserMap and useActiveUsers each
// run through their own createIncrementalMapSelector, which produces
// independent RenderUserIdentity object refs even for the same user.
// A shared cache would always miss because the `prev.src === identity`
// check compares refs across the two selectors.
const renderUserMapMergeCache = new Map<
  string,
  { src: RenderUserIdentity; emoji: string | null; merged: RenderUser }
>();
const activeUsersListMergeCache = new Map<
  string,
  { src: RenderUserIdentity; emoji: string | null; merged: RenderUser }
>();
const activeUserMergeCache = new Map<
  string,
  {
    src: ActiveUserIdentity;
    is_online: boolean;
    last_active_at: number | null;
    status_emoji: string | null;
    merged: ActiveUser;
  }
>();

const EMPTY_USER_PREVIEWS: {
  id: string;
  display_name: string;
  avatar_url?: any;
  is_deactivated?: any;
}[] = [];
const renderUserMapSelector = createRenderUserMapSelector();
const activeUsersSelector = createActiveUsersSelector();
const userPreviewListSelector = createUserPreviewListSelector();

// Active user map: includes is_online for presence sorting (DMList).
// Identity-only projection (hot fields merged in at hook level).
const activeUserMapSelector = createIncrementalMapSelector(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (u: any) => u.id as string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (u: any): ActiveUserIdentity => ({
    id: u.id,
    display_name: u.display_name,
    avatar_url: u.avatar_url,
    is_coworker: u.is_coworker,
    is_deactivated: u.is_deactivated,
    is_guest: u.is_guest,
    email: u.email,
  }),
  (prev, next) =>
    prev.display_name === next.display_name &&
    prev.avatar_url === next.avatar_url &&
    prev.is_coworker === next.is_coworker &&
    prev.is_deactivated === next.is_deactivated &&
    prev.is_guest === next.is_guest &&
    prev.email === next.email,
);

/**
 * Identity-stable Map of render-relevant user fields.
 *
 * Phase 1.5.3 — `status_emoji` is sourced from `user_presence` rather
 * than `users.X` (the legacy column will be dropped in PR 1.5.6).
 *
 * Stability guarantees (preserved across the migration):
 *   - The OUTER map reference is stable across typing heartbeats and
 *     last_active_at updates. This hook subscribes to a narrow
 *     `useUserStatusEmojiMap` (status_emoji-only) that doesn't fire on
 *     heartbeats — NOT the full `useUserPresenceMap` which does. So
 *     consumers doing `useMemo([users])` upstream don't re-fire on
 *     keystrokes from anyone in the workspace.
 *   - PER-ENTRY references are stable when neither the source identity
 *     ref nor the user's status_emoji has changed. The merge cache
 *     reuses the prior merged object in that case.
 */
export function useRenderUserMap(): [
  Map<string, RenderUser>,
  { type: string },
] {
  const { user } = useCurrentUser();
  const [identityMap, status] = useZeroSelector(
    CACHE_KEY,
    user ? queries.users.all() : null,
    renderUserMapSelector,
    {
      defaultValue: EMPTY_RENDER_USER_IDENTITY_MAP,
    },
  );
  // Phase 1.5.3 — depend on useUserStatusEmojiMap (stable across
  // typing/last_active heartbeats) instead of useUserPresenceMap
  // (churns on every heartbeat). status_emoji is the only presence
  // field this hook merges, so the narrower subscription preserves
  // useRenderUserMap's "stable across typing heartbeats" contract.
  const [statusEmojiMap] = useUserStatusEmojiMap();
  const merged = useMemo<Map<string, RenderUser>>(
    () =>
      mergeRenderUserMap(identityMap, statusEmojiMap, renderUserMapMergeCache),
    [identityMap, statusEmojiMap],
  );
  // `mergeRenderUserMap` reuses per-entry refs but always allocates a
  // fresh outer Map. `useStableMap` returns the previous Map reference
  // when every entry is pointer-equal — closing the "outer churn,
  // zero real changes" cascade through MessageArea consumers.
  const stable = useStableMap(merged);
  useMapChurnLog("renderUserMap", stable);
  return [stable, status];
}

/**
 * Single user lookup. Stable unless that user's render fields changed.
 */
export function useRenderUser(
  userId: string | undefined,
): RenderUser | undefined {
  const [map] = useRenderUserMap();
  return userId ? map.get(userId) : undefined;
}

/**
 * Active (non-deactivated) users as RenderUser[].
 *
 * Phase 1.5.3 — identity comes from users.all (selector projects
 * RenderUserIdentity); status_emoji comes from user_presence and is
 * merged in here. Each entry's reference stays stable when neither
 * the source identity nor the user's status_emoji has changed.
 */
export function useActiveUsers(): [RenderUser[], { type: string }] {
  const { user } = useCurrentUser();
  const [identityList, status] = useZeroSelector(
    CACHE_KEY,
    user ? queries.users.all() : null,
    activeUsersSelector,
    {
      defaultValue: EMPTY_RENDER_USER_IDENTITY_LIST,
    },
  );
  // Same heartbeat-stability concern as useRenderUserMap — use the
  // narrow status-emoji selector.
  const [statusEmojiMap] = useUserStatusEmojiMap();
  const merged = useMemo<RenderUser[]>(
    () =>
      mergeRenderUserList(
        identityList,
        statusEmojiMap,
        activeUsersListMergeCache,
      ),
    [identityList, statusEmojiMap],
  );
  return [merged, status];
}

/**
 * Stable preview list: { id, display_name, avatar_url, is_deactivated }[].
 * Includes deactivated users. Stable across typing/presence heartbeats.
 * For CommandPalette and search — NOT raw queries.users.all() objects.
 */
export function useUserList() {
  const { user } = useCurrentUser();
  return useZeroSelector(
    CACHE_KEY,
    user ? queries.users.all() : null,
    userPreviewListSelector,
    {
      defaultValue: EMPTY_USER_PREVIEWS,
    },
  );
}

/**
 * Active user Map including presence fields (is_online, last_active_at).
 * For components that sort by online status (DMList) and member panels.
 *
 * Phase 1.5.3 — `is_online`, `last_active_at`, and `status_emoji` are
 * merged in from `user_presence`. Per-entry references stay stable
 * when neither the source identity NOR any of the merged presence
 * fields has changed. So a typing-heartbeat from user A doesn't
 * change user B's ActiveUser entry reference. The OUTER map reference
 * is also stabilized via `useStableMap` — when every entry is
 * pointer-equal to the previous return, the previous Map ref is
 * returned. Consumers that destructure the tuple (`const [map] =
 * useActiveUserMap()`) only see a new Map identity when an entry
 * actually changed, eliminating the user_presence-heartbeat cascade
 * through `MessageArea` for `typing_in` / `terminal_*` flips that this
 * hook doesn't track.
 */
export function useActiveUserMap(): [
  Map<string, ActiveUser>,
  { type: string },
] {
  const { user } = useCurrentUser();
  const [identityMap, status] = useZeroSelector(
    CACHE_KEY,
    user ? queries.users.all() : null,
    activeUserMapSelector,
    { defaultValue: EMPTY_ACTIVE_USER_IDENTITY_MAP },
  );
  const [presenceMap] = useUserPresenceMap();
  const merged = useMemo<Map<string, ActiveUser>>(
    () => mergeActiveUserMap(identityMap, presenceMap, activeUserMergeCache),
    [identityMap, presenceMap],
  );
  const stable = useStableMap(merged);
  useMapChurnLog("activeUserMap", stable);
  return [stable, status];
}
